import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import TngRewardCanvas from '@/components/survey/TngRewardCanvas';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isSetAProfileComplete } from '@/lib/setAProfileStorage';
import { readTngRewardSession } from '@/lib/tngRewardSession';
import { surveyStepRedirect } from '@/lib/surveyFlow';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

/** Browser back/forward POP from /tng → home (BrowserRouter has no useBlocker). */
function TngBackToHomeOnPop() {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    const onPopState = () => {
      navigate('/', { replace: true });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigate]);
  return null;
}

function formatExpiryLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export default function Tng() {
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid, questionnaireSet } = useAppSelector(selectOtpDraft);

  const reward = useMemo(() => readTngRewardSession(), []);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/phone" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/phone" replace />;
  }

  /**
   * After survey-submit-tng we call `clearAllLocalStorage()` before `/tng`. Full session +
   * Redux reset runs when leaving `/tng` (see router). `sessionStorage` still holds
   * `tng_reward` until then — skip localStorage-based step checks when set.
   */
  if (reward == null) {
    if (!isSetAProfileComplete()) {
      return <Navigate to="/survey/set-a/profile" replace />;
    }
    const stepRedirect =
      questionnaireSet === 'A' || questionnaireSet === 'B'
        ? surveyStepRedirect(questionnaireSet)
        : '/survey';
    if (stepRedirect) {
      return <Navigate to={stepRedirect} replace />;
    }
  }

  const expiryLabel = formatExpiryLabel(reward?.expirydate ?? null);

  useEffect(() => {
    if (reward != null) {
      clearAllLocalStorage();
    }
  }, [reward]);

  return (
    <>
      <TngBackToHomeOnPop />
      <SurveyBackgroundLayout>
        <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden pt-[7dvh]">
          <div className="shrink-0 px-6 pt-8 text-center sm:pt-10">
            <h1 className="text-[36px] font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[42px]">
              {t.tng.title}
            </h1>
            <p className="mt-2 text-[22px] font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[28px]">
              {t.tng.subtitle}
            </p>
          </div>
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-10 pt-6">
            {reward != null ? (
              <div className="-translate-y-[5%] flex flex-col items-center gap-10 sm:gap-11">
                <TngRewardCanvas
                  pin={reward.pin}
                  valueLabel={reward.value}
                  expiryLabel={expiryLabel}
                />
                <p className="max-w-md px-2 text-center text-[22px] font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[28px]">
                  {t.tng.smsNote}
                </p>
              </div>
            ) : (
              <div className="-translate-y-[5%] flex flex-col items-center">
                <img
                  src="/images/TnG.jpg"
                  alt=""
                  className="max-h-[min(65.78vh,557px)] w-full max-w-[567px] object-contain"
                />
                <p className="mt-4 max-w-md text-center text-[14px] font-bold leading-snug text-white/85">
                  {t.tng.fallbackNote}
                </p>
              </div>
            )}
          </div>
        </div>
      </SurveyBackgroundLayout>
    </>
  );
}
